import { create } from 'zustand';
import type { Category, Blahblah, FeedSortType, FollowedThread } from '@/types';
import * as categoriesApi from '@/api/categories';
import * as blahblahsApi from '@/api/blahblahs';

interface CategoryState {
  // Categories list
  categories: Category[];
  categoriesLoading: boolean;
  categoriesError: string | null;

  // Category feed
  feedItems: Blahblah[];
  currentSlug: string | null;
  currentSort: FeedSortType;
  feedNextCursor: string | null;
  feedHasMore: boolean;
  feedLoading: boolean;
  feedLoadingMore: boolean;
  feedError: string | null;

  // Followed threads
  followedThreads: FollowedThread[];
  followedNextCursor: string | null;
  followedHasMore: boolean;
  followedLoading: boolean;
  followedLoadingMore: boolean;
  followedError: string | null;

  // Category actions
  loadCategories: () => Promise<void>;
  starCategory: (id: number) => Promise<void>;
  unstarCategory: (id: number) => Promise<void>;

  // Category feed actions
  loadCategoryFeed: (slug: string, sort?: FeedSortType) => Promise<void>;
  loadMoreCategoryFeed: () => Promise<void>;
  refreshCategoryFeed: () => Promise<void>;
  setFeedSort: (sort: FeedSortType) => void;

  // Feed item actions (like/unlike/delete)
  likeFeedItem: (id: string) => Promise<void>;
  unlikeFeedItem: (id: string) => Promise<void>;
  deleteFeedItem: (id: string) => Promise<void>;

  // Thread follow actions
  loadFollowedThreads: () => Promise<void>;
  loadMoreFollowedThreads: () => Promise<void>;
  followThread: (blahblahId: string, notificationsEnabled?: boolean) => Promise<void>;
  unfollowThread: (blahblahId: string) => Promise<void>;
  toggleThreadNotifications: (blahblahId: string, enabled: boolean) => Promise<void>;
}

export const useCategoryStore = create<CategoryState>((set, get) => ({
  // Initial state
  categories: [],
  categoriesLoading: false,
  categoriesError: null,

  feedItems: [],
  currentSlug: null,
  currentSort: 'latest',
  feedNextCursor: null,
  feedHasMore: true,
  feedLoading: false,
  feedLoadingMore: false,
  feedError: null,

  followedThreads: [],
  followedNextCursor: null,
  followedHasMore: true,
  followedLoading: false,
  followedLoadingMore: false,
  followedError: null,

  // ==================== CATEGORIES ====================

  loadCategories: async () => {
    set({ categoriesLoading: true, categoriesError: null });
    try {
      const categories = await categoriesApi.getCategories();
      set({ categories, categoriesLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load categories';
      set({ categoriesError: message, categoriesLoading: false });
    }
  },

  starCategory: async (id) => {
    // Optimistic update
    set((state) => ({
      categories: state.categories.map((cat) =>
        cat.id === id ? { ...cat, isStarred: true } : cat
      ),
    }));

    try {
      await categoriesApi.starCategory(id);
    } catch {
      // Revert on error
      set((state) => ({
        categories: state.categories.map((cat) =>
          cat.id === id ? { ...cat, isStarred: false } : cat
        ),
      }));
    }
  },

  unstarCategory: async (id) => {
    // Optimistic update
    set((state) => ({
      categories: state.categories.map((cat) =>
        cat.id === id ? { ...cat, isStarred: false } : cat
      ),
    }));

    try {
      await categoriesApi.unstarCategory(id);
    } catch {
      // Revert on error
      set((state) => ({
        categories: state.categories.map((cat) =>
          cat.id === id ? { ...cat, isStarred: true } : cat
        ),
      }));
    }
  },

  // ==================== CATEGORY FEED ====================

  loadCategoryFeed: async (slug, sort = 'latest') => {
    set({
      currentSlug: slug,
      currentSort: sort,
      feedLoading: true,
      feedError: null,
      feedItems: [],
    });

    try {
      const response = await categoriesApi.getCategoryFeed(slug, undefined, 20, sort);
      set({
        feedItems: response.items,
        feedNextCursor: response.nextCursor,
        feedHasMore: response.hasMore,
        feedLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load category feed';
      set({ feedError: message, feedLoading: false });
    }
  },

  loadMoreCategoryFeed: async () => {
    const { currentSlug, currentSort, feedNextCursor, feedHasMore, feedLoadingMore } = get();
    if (!feedHasMore || feedLoadingMore || !feedNextCursor || !currentSlug) return;

    set({ feedLoadingMore: true });

    try {
      const response = await categoriesApi.getCategoryFeed(currentSlug, feedNextCursor, 20, currentSort);
      set((state) => ({
        feedItems: [...state.feedItems, ...response.items],
        feedNextCursor: response.nextCursor,
        feedHasMore: response.hasMore,
        feedLoadingMore: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load more';
      set({ feedError: message, feedLoadingMore: false });
    }
  },

  refreshCategoryFeed: async () => {
    const { currentSlug, currentSort } = get();
    if (!currentSlug) return;

    set({ feedLoading: true, feedError: null });

    try {
      const response = await categoriesApi.getCategoryFeed(currentSlug, undefined, 20, currentSort);
      set({
        feedItems: response.items,
        feedNextCursor: response.nextCursor,
        feedHasMore: response.hasMore,
        feedLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to refresh';
      set({ feedError: message, feedLoading: false });
    }
  },

  setFeedSort: (sort) => {
    const { currentSlug } = get();
    if (currentSlug) {
      get().loadCategoryFeed(currentSlug, sort);
    }
  },

  // ==================== FEED ITEM ACTIONS ====================

  likeFeedItem: async (id) => {
    // Optimistic update
    set((state) => ({
      feedItems: state.feedItems.map((item) =>
        item.id === id
          ? { ...item, isLiked: true, likesCount: item.likesCount + 1 }
          : item
      ),
    }));

    try {
      await blahblahsApi.likeBlahblah(id);
    } catch {
      // Revert on error
      set((state) => ({
        feedItems: state.feedItems.map((item) =>
          item.id === id
            ? { ...item, isLiked: false, likesCount: item.likesCount - 1 }
            : item
        ),
      }));
    }
  },

  unlikeFeedItem: async (id) => {
    // Optimistic update
    set((state) => ({
      feedItems: state.feedItems.map((item) =>
        item.id === id
          ? { ...item, isLiked: false, likesCount: item.likesCount - 1 }
          : item
      ),
    }));

    try {
      await blahblahsApi.unlikeBlahblah(id);
    } catch {
      // Revert on error
      set((state) => ({
        feedItems: state.feedItems.map((item) =>
          item.id === id
            ? { ...item, isLiked: true, likesCount: item.likesCount + 1 }
            : item
        ),
      }));
    }
  },

  deleteFeedItem: async (id) => {
    const { feedItems } = get();
    const itemToDelete = feedItems.find((item) => item.id === id);

    // Optimistic update
    set((state) => ({
      feedItems: state.feedItems.filter((item) => item.id !== id),
    }));

    try {
      await blahblahsApi.deleteBlahblah(id);
    } catch {
      // Revert on error
      if (itemToDelete) {
        set((state) => ({
          feedItems: [...state.feedItems, itemToDelete],
        }));
      }
    }
  },

  // ==================== THREAD FOLLOWING ====================

  loadFollowedThreads: async () => {
    set({ followedLoading: true, followedError: null });

    try {
      const response = await categoriesApi.getFollowedThreads();
      set({
        followedThreads: response.items,
        followedNextCursor: response.nextCursor,
        followedHasMore: response.hasMore,
        followedLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load followed threads';
      set({ followedError: message, followedLoading: false });
    }
  },

  loadMoreFollowedThreads: async () => {
    const { followedNextCursor, followedHasMore, followedLoadingMore } = get();
    if (!followedHasMore || followedLoadingMore || !followedNextCursor) return;

    set({ followedLoadingMore: true });

    try {
      const response = await categoriesApi.getFollowedThreads(followedNextCursor);
      set((state) => ({
        followedThreads: [...state.followedThreads, ...response.items],
        followedNextCursor: response.nextCursor,
        followedHasMore: response.hasMore,
        followedLoadingMore: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load more';
      set({ followedError: message, followedLoadingMore: false });
    }
  },

  followThread: async (blahblahId, notificationsEnabled = true) => {
    try {
      await categoriesApi.followThread(blahblahId, notificationsEnabled);
      // Refresh the list to get updated data
      get().loadFollowedThreads();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to follow thread';
      set({ followedError: message });
    }
  },

  unfollowThread: async (blahblahId) => {
    // Optimistic update
    set((state) => ({
      followedThreads: state.followedThreads.filter(
        (t) => t.blahblah.id !== blahblahId
      ),
    }));

    try {
      await categoriesApi.unfollowThread(blahblahId);
    } catch {
      // Revert on error - refresh the list
      get().loadFollowedThreads();
    }
  },

  toggleThreadNotifications: async (blahblahId, enabled) => {
    // Optimistic update
    set((state) => ({
      followedThreads: state.followedThreads.map((t) =>
        t.blahblah.id === blahblahId
          ? { ...t, notificationsEnabled: enabled }
          : t
      ),
    }));

    try {
      await categoriesApi.updateThreadFollow(blahblahId, enabled);
    } catch {
      // Revert on error
      set((state) => ({
        followedThreads: state.followedThreads.map((t) =>
          t.blahblah.id === blahblahId
            ? { ...t, notificationsEnabled: !enabled }
            : t
        ),
      }));
    }
  },
}));
